import {Injectable} from "@angular/core";
import {AlertController} from "@ionic/angular";
import {AlertInput} from "@ionic/core/dist/types/components/alert/alert-interface";

export interface MessageOptions {
    title?: string;
    message?: string;
}

export interface ConfirmOptions {
    title?: string;
    message?: string;
    cancelButtonText?: string;
    okButtonText?: string;
}

export interface ChooseOptions {
    title?: string;
    message?: string;
    buttons?: Array<string>;
}

export interface CodeOptions {
    title?: string;
    message?: string;
    cancelButtonText?: string;
    okButtonText?: string;
}

export interface InputOptions {
    title?: string;
    message?: string;
    inputs: Array<AlertInput>;
    cancelButtonText?: string;
    okButtonText?: string;
}

export interface SelectOptions {
    title?: string;
    message?: string;
    cancelButtonText?: string;
    okButtonText?: string;
    options?: Array<{ value?: any, label?: string, type?: "radio", checked?: boolean }>;
    option?: any;
}

@Injectable()
export class DialogService {
    constructor(
        private readonly alertCtrl: AlertController
    ) {
        // nothing
    }

    message(options: MessageOptions): Promise<any> {
        return new Promise((resolve) => {
            this.alertCtrl.create({
                header: options.title,
                message: options.message,
                buttons: [
                    {
                        text: "Close",
                        role: 'cancel',
                        handler: () => {
                            resolve(false);
                        }
                    },
                ]
            }).then(result => {
                result.present();
                result.onDidDismiss().then(() => resolve(false));
            });
        });
    }


    confirm(options: ConfirmOptions): Promise<any> {
        return new Promise((resolve) => {
            this.alertCtrl.create({
                header: options.title,
                message: options.message,
                buttons: [
                    {
                        text: options.cancelButtonText,
                        role: 'cancel',
                        handler: () => {
                            resolve(false);
                        }
                    },
                    {
                        text: options.okButtonText,
                        handler: () => {
                            resolve(true);
                        }
                    }
                ]
            }).then(result => {
                result.present();
                result.onDidDismiss().then(() => resolve(false));
            });
        });
    }

    code(options: CodeOptions): Promise<string> {
        const inputs: Array<AlertInput> = [
            {
                name: "code",
                value: "",
                type: "text",
                placeholder: "######",
                label: "6-digit code",
                attributes: {
                    maxlength: 6,
                },
                cssClass: "verify-code",
            }
        ];
        return new Promise((resolve) => {
            this.alertCtrl.create({
                header: options.title,
                message: options.message,
                inputs: inputs,
                buttons: [
                    {
                        text: options.cancelButtonText,
                        role: 'cancel',
                        handler: () => {
                            resolve(null);
                        }
                    },
                    {
                        text: options.okButtonText,
                        handler: (input) => {
                            resolve(input.code);
                        }
                    }
                ]
            }).then(result => {
                result.present();
                result.onDidDismiss().then(() => resolve(null));
            });
        });
    }

    input(options: InputOptions): Promise<any> {
        return new Promise((resolve) => {
            this.alertCtrl.create({
                header: options.title,
                message: options.message,
                inputs: options.inputs,
                buttons: [
                    {
                        text: options.cancelButtonText,
                        role: 'cancel',
                        handler: () => {
                            resolve(null);
                        }
                    },
                    {
                        text: options.okButtonText,
                        handler: (input) => {
                            resolve(input);
                        }
                    }
                ]
            }).then(result => {
                result.present();
                result.onDidDismiss().then(() => resolve(null));
            });
        });
    }

    choose(options: ChooseOptions): Promise<any> {
        return new Promise((resolve) => {
            this.alertCtrl.create({
                header: options.title,
                message: options.message,
                buttons: options.buttons.map((button, index) => {
                    return {
                        text: button,
                        handler: () => {
                            resolve(button);
                        }
                    }
                })
            }).then(result => {
                result.present();
                result.onDidDismiss().then(() => resolve(false));
            });
        });
    }

    select(options: SelectOptions): Promise<any> {
        options.options.forEach(o => {
            if (o.value === options.option) o.checked = true;
            o.type = "radio";
        });
        if (!options.cancelButtonText) options.cancelButtonText = "Cancel";
        if (!options.okButtonText) options.okButtonText = "Ok";

        return new Promise((resolve) => {
            this.alertCtrl.create({
                header: options.title,
                message: options.message,
                inputs: options.options,
                buttons: [
                    {
                        text: options.cancelButtonText,
                        role: 'cancel',
                        handler: () => {
                            resolve(null);
                        }
                    },
                    {
                        text: options.okButtonText,
                        handler: (data) => {
                            resolve(data);
                        }
                    }
                ]
            }).then(result => {
                result.present();
                result.onDidDismiss().then(() => resolve(false));
            });
        });
    }

    dismiss(): Promise<boolean> {
        return this.alertCtrl.dismiss();
    }
}
